import React from 'react';
import { useDispatch } from 'react-redux';
import { UploadFile } from 'antd/lib/upload/interface';
import { AppSettingsUpdatable } from '@/actions/updatable';
import {
    RemoveDefaultReviewAttachmentRequest,
    tryUpdateAppSettings,
    UploadDefaultReviewAttachmentRequest,
} from '@/actions/appSettings';
import { globalMessage } from '@/utils/globalMessage';
import Typography from 'antd/lib/typography';
import { FileDeleteDialog } from '@/components/appSettings/FileUpload/FileDeleteDialog';
import { FileUploadDialog } from '@/components/appSettings/FileUpload/FileUploadDialog';
import { UploadFooterText } from '@/components/appSettings/FileUpload/UploadFooterText';
import { FileStats } from '@/types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const acceptedFileTypes = [
    '.pdf',
    '.doc',
    '.docx',
    '.rtf',
    '.pages',
    '.xls',
    '.xlsx',
    '.numbers',
    '.zip',
    '.rar',
    '.plist',
    '.crash',
    '.jpg',
    '.png',
    '.mp4',
    '.avi',
];

interface DefaultReviewAttachmentUploadProps {
    appId: string;
    orgId: string;
    stepConfigId?: string;
    currentlyUploadedReviewAttachment?: FileStats;
    onSetAttachmentFile?: () => void;
    onRemoveAttachmentFile?: () => void;
}

function DefaultReviewAttachmentUpload({
    appId,
    orgId,
    stepConfigId,
    currentlyUploadedReviewAttachment,
    onSetAttachmentFile,
    onRemoveAttachmentFile,
}: DefaultReviewAttachmentUploadProps) {
    const dispatch = useDispatch();

    const [stagedFile, setStagedFile] = React.useState<UploadFile[]>([]);

    //UPLOAD

    const uploadReviewAttachmentIsLoading = useIsLoading(AppSettingsUpdatable.UploadDefaultReviewAttachment);
    const uploadReviewAttachmentError = useError(AppSettingsUpdatable.UploadDefaultReviewAttachment);
    const uploadReviewAttachmentSuccess = useUpdated(AppSettingsUpdatable.UploadDefaultReviewAttachment);

    const handleUploadReviewAttachment = (file) => {
        const request: UploadDefaultReviewAttachmentRequest = {
            appId,
            orgId,
            file: file,
            stepConfigId,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UploadDefaultReviewAttachment, request));
    };

    React.useEffect(() => {
        if (uploadReviewAttachmentSuccess) {
            globalMessage.success('Default review attachment uploaded!');
            if (onSetAttachmentFile != undefined) onSetAttachmentFile();
        }
    }, [uploadReviewAttachmentSuccess]);

    React.useEffect(() => {
        if (uploadReviewAttachmentError) {
            globalMessage.error(uploadReviewAttachmentError);
        }
    }, [uploadReviewAttachmentError]);

    //DELETE

    const deleteReviewAttachmentIsLoading = useIsLoading(AppSettingsUpdatable.RemoveDefaultReviewAttachment);
    const deleteReviewAttachmentError = useError(AppSettingsUpdatable.RemoveDefaultReviewAttachment);
    const deleteReviewAttachmentSuccess = useUpdated(AppSettingsUpdatable.RemoveDefaultReviewAttachment);

    const removeReviewAttachment = (fileName) => {
        const request: RemoveDefaultReviewAttachmentRequest = {
            orgId,
            appId,
            fileName,
            stepConfigId,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RemoveDefaultReviewAttachment, request));
    };

    React.useEffect(() => {
        if (deleteReviewAttachmentSuccess) {
            globalMessage.success('Default review attachment removed!');
            setStagedFile([]);
            if (onRemoveAttachmentFile != undefined) onRemoveAttachmentFile();
        }
    }, [deleteReviewAttachmentSuccess]);

    React.useEffect(() => {
        if (deleteReviewAttachmentError) {
            globalMessage.error(deleteReviewAttachmentError);
        }
    }, [deleteReviewAttachmentError]);

    const showDelete = !!currentlyUploadedReviewAttachment;

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography.Text>
                    Upload the file attachment to include in the review information for each release.
                </Typography.Text>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        paddingTop: showDelete ? '12px' : '24px',
                        paddingBottom: showDelete ? '12px' : '24px',
                    }}
                >
                    {showDelete ?
                        <FileDeleteDialog
                            fileStats={currentlyUploadedReviewAttachment}
                            handleDelete={removeReviewAttachment}
                            isLoading={deleteReviewAttachmentIsLoading}
                            removeButtonText={'Remove File'}
                            confirmationMessage={'Are you sure you want to delete this attachment?'}
                            confirmationAcceptText={'Yes, remove'}
                            confirmationCancelText={'No'}
                        />
                    :   <FileUploadDialog
                            file={stagedFile}
                            acceptedFileTypes={acceptedFileTypes}
                            handleUpload={handleUploadReviewAttachment}
                            isLoading={uploadReviewAttachmentIsLoading}
                        />
                    }
                </div>
                <div style={{ marginBottom: '-24px' }}>
                    <UploadFooterText acceptedFileTypes={acceptedFileTypes} showFileTypes={!showDelete} />
                </div>
            </div>
        </>
    );
}

export default DefaultReviewAttachmentUpload;
